import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, Clock, Info, Save, Trash2, X } from 'lucide-react';
import toast from '../../design-system/toast';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useOrganization } from '../../contexts/OrganizationContext';

const SCAN_TYPES = [
  { value: 'semgrep_scan', label: 'Semgrep' },
  { value: 'secrets_scan', label: 'Secrets' },
  { value: 'deep_ai_scan', label: 'Deep AI Scan' },
];

const QUICK_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (Mon)' },
  { value: 'monthly', label: 'Monthly (1st)' },
];

const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour.toString(),
  label: `${hour.toString().padStart(2, '0')}:00`,
}));

type ScanSchedule = {
  id: string;
  scan_type: string;
  cron_expression: string;
  enabled: boolean;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_run_status?: string | null;
  last_error?: string | null;
};

type ScanScheduleListResponse = {
  schedules: ScanSchedule[];
  timezone: string;
};

interface ApplicationScanScheduleSectionProps {
  organizationId: string;
  applicationId: string;
}

const ApplicationScanScheduleSection: React.FC<ApplicationScanScheduleSectionProps> = ({
  organizationId,
  applicationId,
}) => {
  const api = useAuthenticatedApi();
  const { isAdmin } = useOrganization();

  const [schedules, setSchedules] = useState<ScanSchedule[]>([]);
  const [timezone, setTimezone] = useState('UTC');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCronHelp, setShowCronHelp] = useState(false);

  const [selectedScanType, setSelectedScanType] = useState(SCAN_TYPES[0].value);
  const [cronExpression, setCronExpression] = useState('0 2 * * *');
  const [enabled, setEnabled] = useState(true);
  const [quickFrequency, setQuickFrequency] = useState('daily');
  const [quickHour, setQuickHour] = useState('2');

  const scheduleMap = useMemo(() => {
    return schedules.reduce<Record<string, ScanSchedule>>((acc, schedule) => {
      acc[schedule.scan_type] = schedule;
      return acc;
    }, {});
  }, [schedules]);

  const loadSchedules = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<ScanScheduleListResponse>(
        apiEndpoints.applications.scanSchedules(organizationId, applicationId)
      );
      setSchedules(response.schedules || []);
      setTimezone(response.timezone || 'UTC');
    } catch (err: any) {
      console.error('Failed to load scan schedules:', err);
      toast.error(err.data?.detail || err.message || 'Failed to load scan schedules');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, applicationId]);

  useEffect(() => {
    const schedule = scheduleMap[selectedScanType];
    if (schedule) {
      setCronExpression(schedule.cron_expression);
      setEnabled(schedule.enabled);
    } else {
      setCronExpression('0 2 * * *');
      setEnabled(true);
    }
  }, [scheduleMap, selectedScanType]);

  const formatRunTime = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-US', { timeZone: timezone });
  };

  const formatStatus = (value?: string | null) => {
    if (!value) return '-';
    return value.replace('_', ' ').replace(/^\w/, (char) => char.toUpperCase());
  };

  const statusClass = (value?: string | null) => {
    if (value === 'failed') return 'text-semantic-error';
    if (value === 'skipped') return 'text-semantic-warning';
    if (value === 'triggered') return 'text-semantic-success';
    return 'text-ui-text-tertiary';
  };

  const saveSchedule = async () => {
    if (!isAdmin) return;
    try {
      setIsSaving(true);
      await api.put(
        apiEndpoints.applications.scanSchedule(organizationId, applicationId, selectedScanType),
        {
          cron_expression: cronExpression.trim(),
          enabled,
        }
      );
      await loadSchedules();
      toast.success('Schedule saved');
    } catch (err: any) {
      console.error('Failed to save schedule:', err);
      toast.error(err.data?.detail || err.message || 'Failed to save schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const buildQuickCron = (frequency: string, hour: string) => {
    const hourValue = Number(hour);
    if (frequency === 'weekly') {
      return `0 ${hourValue} * * 1`;
    }
    if (frequency === 'monthly') {
      return `0 ${hourValue} 1 * *`;
    }
    return `0 ${hourValue} * * *`;
  };

  const applyQuickSchedule = () => {
    setCronExpression(buildQuickCron(quickFrequency, quickHour));
  };

  const deleteSchedule = async (scanType: string) => {
    if (!isAdmin) return;
    const confirmDelete = window.confirm('Delete this scan schedule?');
    if (!confirmDelete) return;

    try {
      await api.delete(apiEndpoints.applications.scanSchedule(organizationId, applicationId, scanType));
      await loadSchedules();
      toast.success('Schedule deleted');
    } catch (err: any) {
      console.error('Failed to delete schedule:', err);
      toast.error(err.data?.detail || err.message || 'Failed to delete schedule');
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-ui-text-primary flex items-center gap-2">
            <CalendarClock className="w-5 h-5" />
            Scheduled Scans
          </h2>
          <p className="text-xs text-ui-text-tertiary mt-1">
            Runs in {timezone} timezone. Cron format: minute hour day month day-of-week.
          </p>
        </div>
      </div>

      <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-6 space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr] gap-4">
            <div className="flex flex-col">
              <label className="block text-xs font-medium text-ui-text-secondary mb-2">
                Scan Type
              </label>
              <select
                value={selectedScanType}
                onChange={(e) => setSelectedScanType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm text-ui-text-primary"
                disabled={!isAdmin}
              >
                {SCAN_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-xs font-medium text-ui-text-secondary mb-2">
                Cron Expression
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm text-ui-text-primary"
                  disabled={!isAdmin}
                />
                <button
                  type="button"
                  onClick={() => setShowCronHelp(true)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-full text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
                  aria-label="Cron syntax help"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-ui-text-secondary">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  disabled={!isAdmin}
                  className="rounded border-ui-border-primary"
                />
                Enabled
              </label>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-ui-text-secondary mb-2">
              Quick Picks
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr] gap-4">
              <select
                value={quickFrequency}
                onChange={(e) => setQuickFrequency(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm text-ui-text-primary"
                disabled={!isAdmin}
              >
                {QUICK_FREQUENCIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={quickHour}
                onChange={(e) => setQuickHour(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm text-ui-text-primary"
                disabled={!isAdmin}
              >
                {HOURS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="flex items-end">
                <button
                  onClick={applyQuickSchedule}
                  disabled={!isAdmin}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
            <p className="text-xs text-ui-text-tertiary mt-2">
              Weekly runs use Monday; monthly runs use the 1st.
            </p>
          </div>

          <button
            onClick={saveSchedule}
            disabled={isSaving || !isAdmin}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-black text-sm font-medium hover:bg-brand-primaryDark disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>

        <div className="border-t border-ui-border-primary pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-ui-text-primary">
            Active Schedules
          </h3>
          {isLoading ? (
            <p className="text-sm text-ui-text-tertiary">Loading schedules...</p>
          ) : schedules.length === 0 ? (
            <p className="text-sm text-ui-text-tertiary">No schedules configured.</p>
          ) : (
            <div className="space-y-2">
              {schedules.map((schedule) => {
                const label = SCAN_TYPES.find((item) => item.value === schedule.scan_type)?.label
                  || schedule.scan_type;
                return (
                  <div
                    key={schedule.id}
                    className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 border border-ui-border-primary rounded-lg px-4 py-3"
                  >
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-ui-text-primary">
                          {label}
                        </p>
                        {schedule.enabled ? (
                          <span className="inline-flex items-center gap-1 text-xs text-semantic-success">
                            <CheckCircle2 className="w-3 h-3" />
                            Enabled
                          </span>
                        ) : (
                          <span className="text-xs text-ui-text-tertiary">Paused</span>
                        )}
                      </div>
                      <p className="text-xs text-ui-text-tertiary font-mono break-all">
                        Cron: {schedule.cron_expression}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ui-text-tertiary">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Next: {formatRunTime(schedule.next_run_at)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          Last: {formatRunTime(schedule.last_run_at)}
                        </span>
                        <span className={`inline-flex items-center gap-1 ${statusClass(schedule.last_run_status)}`}>
                          Status: {formatStatus(schedule.last_run_status)}
                        </span>
                      </div>
                      {schedule.last_error && (
                        <p className="text-xs text-semantic-error mt-1 break-words">
                          {schedule.last_error}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteSchedule(schedule.scan_type)}
                      disabled={!isAdmin}
                      className="self-end sm:self-start p-2 rounded-lg hover:bg-ui-background-tertiary text-ui-text-secondary flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      aria-label={`Delete ${label} schedule`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCronHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(event) => {
            if (event.currentTarget === event.target) {
              setShowCronHelp(false);
            }
          }}
        >
          <div
            className="w-full max-w-lg rounded-xl border border-ui-border-primary bg-ui-background-secondary shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Cron syntax help"
          >
            <div className="flex items-center justify-between border-b border-ui-border-primary px-5 py-4">
              <h3 className="text-sm font-semibold text-ui-text-primary">
                Cron Syntax Guide
              </h3>
              <button
                type="button"
                onClick={() => setShowCronHelp(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm text-ui-text-secondary">
              <p>
                Cron uses 5 fields: <span className="font-mono">minute hour day month day-of-week</span>.
              </p>
              <div className="grid grid-cols-1 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="font-medium text-ui-text-secondary">minute</span>
                  <span>0-59</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-ui-text-secondary">hour</span>
                  <span>0-23</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-ui-text-secondary">day</span>
                  <span>1-31</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-ui-text-secondary">month</span>
                  <span>1-12</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-ui-text-secondary">day-of-week</span>
                  <span>0-6 (Sun-Sat)</span>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <p className="font-medium text-ui-text-secondary">Examples</p>
                <p><span className="font-mono">0 2 * * *</span> - every day at 02:00</p>
                <p><span className="font-mono">0 2 * * 1</span> - Mondays at 02:00</p>
                <p><span className="font-mono">0 2 1 * *</span> - 1st of the month at 02:00</p>
              </div>
              <p className="text-xs text-ui-text-tertiary">
                Use * for any value and comma-separated values for multiple times.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationScanScheduleSection;
